import { buildCollection } from "@firecms/core";

import { cleanUpReplacedUploads } from "../uploadCleanup";
import { MAX, cards, ctaLabel, eyebrow, heading, photoRow, section, subheading } from "./sections";

// The Services & Booking page: one document (`pages/services`). Sections are
// declared in page order. Card symbols and colours, row layout, button targets
// and the intake form address stay in the site's code; see
// docs/cms-content-model.md.

/** The editable text on one session card. Shared by both rows. */
const serviceCardFields = {
  title: {
    name: "Session name",
    dataType: "string" as const,
    description: "The large name on the card, e.g. Animal Communication.",
    validation: { max: MAX.cardTitle },
  },
  subtitle: {
    name: "Who it is for",
    dataType: "string" as const,
    description:
      "The small coloured line under the name, e.g. For One Animal.",
    validation: { max: MAX.cardTitle },
  },
  desc: {
    name: "Description",
    dataType: "string" as const,
    multiline: true,
    description: "The paragraph describing what happens in this session.",
    validation: { max: MAX.cardBody },
  },
  tag: {
    name: "Badge (optional)",
    dataType: "string" as const,
    description:
      "A short badge across the top corner of the card, e.g. Most popular. Leave this empty for no badge — most cards have none.",
    validation: { max: 40 },
  },
};

export const servicesPageCollection = buildCollection({
  id: "page-services",
  path: "pages",
  name: "Services & Booking",
  singularName: "Services page",
  icon: "CalendarMonth",
  group: "Pages",
  description:
    "Every piece of text on the Services & Booking page, listed in the order it appears on the page.",
  // `pages` holds one document per page; this collection shows only Services'.
  forceFilter: { pageId: ["==", "services"] },
  // The page exists already and the site expects it — it can be edited, but
  // not added to or removed.
  permissions: { read: true, edit: true, create: false, delete: false },
  openEntityMode: "full_screen",
  hideIdFromForm: true,
  // Hidden from the drawer; the `pageShortcut` view in App.tsx opens it directly.
  hideFromNavigation: true,
  inlineEditing: false,
  exportable: false,
  callbacks: cleanUpReplacedUploads,

  properties: {
    hero: section(
      "1. How we work together",
      "The heading, the line of introduction and the two photos at the top of the page.",
      {
        eyebrow: eyebrow(),
        heading: heading("The large title at the top, e.g. How we work together."),
        subheading: subheading(
          "The centred line under the title, explaining that sessions are remote.",
        ),
        photos: photoRow({
          name: "The two photos",
          description:
            "One either side of the title on a computer, side by side under it on a phone.",
          count: 2,
          storagePath: "pages/services",
          shape:
            "It is cropped to a tall rectangle, so use the focus settings to say which part must stay in view.",
        }),
      },
    ),

    sixtyMinute: section(
      "2. 60 Minute Sessions",
      "The first row of session cards, with the title written across the line above them.",
      {
        heading: heading("The title on the line above this row, e.g. 60 Minute Sessions."),
        items: cards({
          name: "The three 60 minute sessions",
          description: "One card each, side by side.",
          count: 3,
          previewProperties: ["title", "subtitle"],
          properties: serviceCardFields,
        }),
        bookLabel: ctaLabel(
          "The wording on the button at the bottom of every card in this row. It takes the visitor to booking.",
        ),
      },
    ),

    ninetyMinute: section(
      "3. 90 Minute Sessions",
      "The second row of session cards.",
      {
        heading: heading("The title on the line above this row, e.g. 90 Minute Sessions."),
        items: cards({
          name: "The five 90 minute sessions",
          description: "One card each, side by side.",
          count: 5,
          previewProperties: ["title", "subtitle"],
          properties: serviceCardFields,
        }),
        bookLabel: ctaLabel(
          "The wording on the button at the bottom of every card in this row. It takes the visitor to booking.",
        ),
      },
    ),

    intake: section(
      "4. Intake form reminder",
      "The reminder and the yellow button at the very bottom of the page.",
      {
        heading: heading(
          "The reminder written across the line, e.g. Don't Forget to Fill Out Your Intake Form After You Book!",
        ),
        ctaLabel: ctaLabel(
          "The wording on the yellow button. The form it opens is set in the site itself.",
        ),
      },
    ),

    // Identifies which page this document is; set once when seeded.
    pageId: {
      name: "Page id",
      dataType: "string",
      disabled: { hidden: true },
    },
    title: {
      name: "Page title",
      dataType: "string",
      disabled: { hidden: true },
    },
  },

  // The order of the panels in the form — matches the live page top to bottom.
  propertiesOrder: ["hero", "sixtyMinute", "ninetyMinute", "intake"],
});
